import { KEYCLOAK_CLIENT_ID, LOGIN_API_URL } from '@portal/config/env';
import {
  getInstance,
  getCleanInstance,
} from '@portal/repositories/securitySdkConfig';

import qs from 'qs';
import axios from 'axios';
import { IOnboardingCafUrlGenerator } from '@portal/models/module';

const URL = '/onboarding';
const URL_V2 = '/v2/onboarding';

const OnboardingApi = {
  getSettings: async () => {
    const instance = getInstance();
    const { data } = await instance.get('/settings');

    return data as models.Settings;
  },

  validateTaxId: async (value: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${URL}/validate-document`, {
      params: { value },
    });

    return data;
  },
  validateEmail: async (value: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${URL}/validate-email`, {
      params: { value },
    });

    return data;
  },

  inviteCode: async (code: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${URL_V2}/validate-invite/${code}`);

    return data;
  },

  onboardingSettings: async () => {
    const instance = getInstance();
    const { data } = await instance.get(`${URL}/settings`);

    return data;
  },

  validateCellphone: async (value: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${URL}/validate-cellphone`, {
      params: { value },
    });

    return data;
  },

  validateCNAE: async (value: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${URL_V2}/cnae?cnae=${value}`);

    return data;
  },

  validateLogin: async (value: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${URL}/validate-login`, {
      params: { value },
    });

    return data;
  },

  onboardingRegisterDevice: async (
    body: models.OnboardingRegisterDeviceRequest
  ) => {
    const instance = getInstance();
    const { data } = await instance.post(`${URL_V2}/register-device`, body);

    return data;
  },

  onboardingValidateToken: async (
    body: models.OnboardingValidateTokenRequest
  ) => {
    const instance = getInstance();
    const { data } = await instance.post(`${URL}/validate-token`, body);

    return data;
  },

  createAccount: async (body: models.OnboardingRequest) => {
    const instance = getInstance();
    const { data } = await instance.post(`${URL_V2}/create-account`, body);

    return data;
  },

  createAccountLogin: async (user: models.AuthRequest) => {
    const instance = getCleanInstance(LOGIN_API_URL);

    const payload = qs.stringify({
      grant_type: 'password',
      client_id: KEYCLOAK_CLIENT_ID,
      username: user.username,
      password: user.password,
    });
    const { data } = await instance.post(LOGIN_API_URL, payload, {
      headers: {
        'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
    });

    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    };
  },

  getAddress: async (cep: string) => {
    const instance = getInstance();
    const { data } = await instance.get('/util/address', {
      params: { cep },
    });

    return data;
  },

  createAddress: async (body: models.Address) => {
    const instance = getInstance();
    const { data } = await instance.post(`${URL_V2}/address`, {
      ...body,
      alias: 'Casa',
      country: 'BR',
    });

    return data;
  },

  getImageUrl: async (extension: string, type: number) => {
    const instance = getInstance();
    const { data } = await instance.get(`${URL}/document-url`, {
      params: { extension, type },
    });

    return data as {
      url: string;
      bucket: string;
    };
  },

  uploadDocument: async (
    documentToUploadUrl: string,
    documentToUpload: File
  ) => {
    const { data } = await axios.put(documentToUploadUrl, documentToUpload, {
      headers: { 'Content-Type': documentToUpload.type },
    });
    return data;
  },

  saveDocument: async (body: models.OnboardingUserPhotoRequest) => {
    const instance = getInstance();
    const { data } = await instance.put(`${URL}/v2/save-document`, body);
    return data;
  },

  addPartner: async (body: models.Partners) => {
    const instance = getInstance();
    const { data } = await instance.post(`${URL_V2}/representatives`, body);
    return data;
  },

  createPin: async (body: models.OnboardingCreatePinRequest) => {
    const instance = getInstance();
    const { data } = await instance.post(`${URL}/pin`, body);

    return data;
  },

  dataRetry: async (body: models.DataRetryRequest) => {
    const instance = getInstance();
    const { data } = await instance.put(`${URL_V2}/data-retry`, {
      ...body,
      ibk: true,
    });
    return data;
  },

  cboPrimary: async () => {
    const instance = getInstance();
    const { data } = await instance.get(`${URL_V2}/cbo`);

    return data;
  },

  getCboOccupation: async (id: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${URL_V2}/cbo/`, {
      params: {
        subOccupation: id,
      },
    });

    return data;
  },

  generateOnboardingUrlCaf: async (
    generateUrlData: IOnboardingCafUrlGenerator
  ) => {
    const instance = getInstance();
    const response = await instance.post(
      `${URL_V2}/generate-onboarding-url`,
      generateUrlData
    );
    const { id, url } = response.data;

    return { onboardingId: id, onboardingUrl: url };
  },

  getCountries: async () => {
    const instance = getInstance();
    const { data } = await instance.get('/util/countries');
    return data;
  },

  getStates: async () => {
    const instance = getInstance();
    const { data } = await instance.get('/util/state');
    return data;
  },

  getCities: async (uf: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`/util/cities/${uf}`);
    return data;
  },

  getWebviewUrl: async () => {
    const instance = getInstance();
    const { data } = await instance.get(`${URL}/documents-validators`);
    return data;
  },
};

export default OnboardingApi;
